import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getResident, listNeighborhoods, type ExternalResidentProfile } from "../../api";
import { useAppStore } from "../../state/app-store";
import { useExternalDataStore } from "../../state/external-data-store";
import { filterClass } from "../../utils/class-utils";
import { TableInteractionNote } from "./shared/TableInteractionNote";
import { isEasyblocksEditorMode } from "../utils/accordion-shell";
import type { ResidentDirectorySectionProps, ResolvedResidentDirectoryItem } from "./types";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 1000;
const RESULTS_PER_PAGE = 20;
const PROFILE_FALLBACK_IMAGE = "/images/users/user_placeholder.png";

type NeighborhoodOption = {
  value: string;
  label: string;
};

function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function getIsResidentDirectoryPath(): boolean {
  if (typeof window === "undefined") return false;
  return normalizePathname(window.location.pathname) === "/resident-directory";
}

function buildVisiblePages(pageCount: number, currentPage: number): Array<number | string> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, idx) => idx + 1);
  }

  const pages: Array<number | string> = [1];
  const start = Math.max(2, currentPage - 2);
  const end = Math.min(pageCount - 1, currentPage + 2);

  if (start > 2) {
    pages.push("ellipsis-start");
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < pageCount - 1) {
    pages.push("ellipsis-end");
  }

  pages.push(pageCount);
  return pages;
}

function getResidentName(item: Partial<ResolvedResidentDirectoryItem> | ExternalResidentProfile | null): string {
  if (!item) return "Resident profile";

  const displayName = String(item.display_name ?? "").trim();
  if (displayName) return displayName;

  const fullName = [item.first_name, item.last_name]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(" ");

  return fullName || "Resident profile";
}

function getResidentRecordId(item: Partial<ResolvedResidentDirectoryItem> | null): string {
  return String(item?.id ?? item?.__id ?? "").trim();
}

export function ResidentDirectorySection(props: ResidentDirectorySectionProps) {
  const {
    title = "Resident Directory",
    bgClass = "text-bg-white",
    paddingYClass = "py-section-md",
  } = props;

  const instanceIdRef = useRef<string>(`resident-directory-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;
  const isInEditor = isEasyblocksEditorMode();
  const isResidentDirectoryPage = useMemo(() => getIsResidentDirectoryPath(), []);
  const documentId = useAppStore((state) => state.documentId);

  const residentState = useExternalDataStore((state) => state.residentDirectory[instanceId]);
  const ensureResidentDirectory = useExternalDataStore((state) => state.ensureResidentDirectory);
  const loadResidentDirectory = useExternalDataStore((state) => state.loadResidentDirectory);
  const resetResidentDirectory = useExternalDataStore((state) => state.resetResidentDirectory);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedResident, setSelectedResident] = useState<ResolvedResidentDirectoryItem | null>(null);
  const [selectedResidentProfile, setSelectedResidentProfile] = useState<ExternalResidentProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [catalogNeighborhoodOptions, setCatalogNeighborhoodOptions] = useState<NeighborhoodOption[]>([]);

  const items = (residentState?.items ?? []) as ResolvedResidentDirectoryItem[];
  const isLoading = residentState?.isLoading ?? false;
  const errorText = residentState?.error ?? null;

  const sectionClasses = [bgClass, filterClass(paddingYClass)]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  const classes = {
    section: ["section section-resident-directory", sectionClasses].filter(Boolean).join(" ").trim(),
    container: "container-xl",
    title: "mb-3",
    searchWrap: "card mb-3",
    searchBody: "card-body",
    searchControls: "row g-3 align-items-end",
    searchTextColumn: "col-12 col-lg-8",
    searchFilterColumn: "col-12 col-lg-4",
    searchLabel: "form-label fw-semibold",
    searchInput: "form-control form-control-sm",
    searchSelect: "form-select form-select-sm",
    helperText: "form-text fs-14",
    loading: "card shadow-sm",
    loadingBody: "card-body d-flex align-items-center gap-2",
    error: "alert alert-warning mb-0",
    info: "alert alert-info mb-0",
    empty: "alert alert-light border mb-0",
    summary: "d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3",
    tableWrap: "table-responsive",
    table: "table table-striped table-hover align-middle mb-0",
    paginationWrap: "d-flex justify-content-center mt-3",
    pagination: "pagination mb-0 flex-wrap",
  };

  const selectedResidentId = getResidentRecordId(selectedResident);
  const selectedResidentName = getResidentName(selectedResidentProfile ?? selectedResident);
  const profileImageSrc = (() => {
    const filename = String(selectedResidentProfile?.attachment_filename ?? "").trim();
    return filename ? `/images/users/${filename}` : PROFILE_FALLBACK_IMAGE;
  })();

  function closeProfileModal() {
    setSelectedResident(null);
    setSelectedResidentProfile(null);
    setIsProfileLoading(false);
    setProfileError(null);
  }

  function openProfileModal(item: ResolvedResidentDirectoryItem) {
    setSelectedResident(item);
  }

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timerId);
  }, [query]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedNeighborhood]);

  useEffect(() => {
    if (isInEditor || !isResidentDirectoryPage) {
      return;
    }

    ensureResidentDirectory(instanceId);
    void loadResidentDirectory(instanceId, 5000);
    return () => resetResidentDirectory(instanceId);
  }, [
    ensureResidentDirectory,
    instanceId,
    isInEditor,
    isResidentDirectoryPage,
    loadResidentDirectory,
    resetResidentDirectory,
  ]);

  useEffect(() => {
    if (isInEditor || !isResidentDirectoryPage) {
      return;
    }

    let cancelled = false;
    void listNeighborhoods(documentId ?? undefined, 200)
      .then((neighborhoods) => {
        if (cancelled) return;
        const values = new Map<string, string>();
        neighborhoods.forEach((item) => {
          const value = String(item.neighborhood_number ?? "").trim();
          const label = String(item.item_name ?? "").trim();
          if (value === "" || value === "0" || label === "") return;
          if (!values.has(value)) {
            values.set(value, label);
          }
        });
        setCatalogNeighborhoodOptions(
          Array.from(values.entries()).map(([value, label]) => ({ value, label }))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setCatalogNeighborhoodOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, isInEditor, isResidentDirectoryPage]);

  useEffect(() => {
    if (!selectedResidentId) {
      return;
    }

    let cancelled = false;
    setIsProfileLoading(true);
    setProfileError(null);
    setSelectedResidentProfile(null);

    void getResident(selectedResidentId, documentId ?? undefined)
      .then((residentProfile) => {
        if (cancelled) return;
        setSelectedResidentProfile(residentProfile);
        setIsProfileLoading(false);
      })
      .catch((error: any) => {
        if (cancelled) return;
        setProfileError(error?.message ?? "Failed to load resident profile");
        setIsProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, selectedResidentId]);

  useEffect(() => {
    if (!selectedResident) {
      return;
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeProfileModal();
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedResident]);

  useEffect(() => {
    if (!selectedResident || typeof document === "undefined") {
      return;
    }

    document.body.classList.add("modal-open");

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [selectedResident]);

  const hasMinimumQuery = debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const hasNeighborhoodFilter = selectedNeighborhood !== "";
  const hasActiveFilters = hasMinimumQuery || hasNeighborhoodFilter;

  const fallbackNeighborhoodOptions = useMemo(() => {
    const values = new Map<string, string>();
    items.forEach((item) => {
      const neighborhood = String(item.neighborhood_number ?? "").trim();
      const neighborhoodName = String(item.neighborhood_name ?? "").trim();
      if (!neighborhood || neighborhood === "0" || !neighborhoodName) return;
      values.set(neighborhood, neighborhoodName || neighborhood);
    });

    return Array.from(values.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [items]);

  const neighborhoodOptions = useMemo(() => {
    if (catalogNeighborhoodOptions.length > 0) {
      return catalogNeighborhoodOptions;
    }
    return fallbackNeighborhoodOptions;
  }, [catalogNeighborhoodOptions, fallbackNeighborhoodOptions]);

  useEffect(() => {
    if (selectedNeighborhood === "") {
      return;
    }
    const stillExists = neighborhoodOptions.some((option) => option.value === selectedNeighborhood);
    if (!stillExists) {
      setSelectedNeighborhood("");
    }
  }, [neighborhoodOptions, selectedNeighborhood]);

  const filteredItems = useMemo(() => {
    if (!hasActiveFilters) return [];

    const queryTokens = debouncedQuery
      .toLowerCase()
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean);

    return items.filter((item) => {
      const neighborhood = String(item.neighborhood_number ?? "").trim();
      const firstName = String(item.first_name ?? "").toLowerCase();
      const lastName = String(item.last_name ?? "").toLowerCase();
      const address = String(item.address ?? "").toLowerCase();
      const matchesQuery =
        !hasMinimumQuery ||
        queryTokens.some((token) =>
          [firstName, lastName, address].some((field) => field.includes(token))
        );
      const matchesNeighborhood = !hasNeighborhoodFilter || neighborhood === selectedNeighborhood;

      return matchesQuery && matchesNeighborhood;
    });
  }, [debouncedQuery, hasActiveFilters, hasMinimumQuery, hasNeighborhoodFilter, items, selectedNeighborhood]);

  const totalResults = filteredItems.length;
  const totalPages = totalResults > 0 ? Math.ceil(totalResults / RESULTS_PER_PAGE) : 0;
  const safeCurrentPage = totalPages > 0 ? Math.min(currentPage, totalPages) : 1;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pagedItems = useMemo(() => {
    if (totalResults === 0) return [];
    const startIdx = (safeCurrentPage - 1) * RESULTS_PER_PAGE;
    return filteredItems.slice(startIdx, startIdx + RESULTS_PER_PAGE);
  }, [filteredItems, safeCurrentPage, totalResults]);

  const rangeStart = totalResults === 0 ? 0 : (safeCurrentPage - 1) * RESULTS_PER_PAGE + 1;
  const rangeEnd = totalResults === 0 ? 0 : Math.min(rangeStart + RESULTS_PER_PAGE - 1, totalResults);
  const visiblePages = buildVisiblePages(totalPages, safeCurrentPage);
  const profileModal =
    selectedResident ? (
      <>
        <div
          className="modal fade show"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${instanceId}-resident-profile-title`}
          style={{ display: "block", zIndex: 20000 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeProfileModal();
            }
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                  <div>
                    <h5
                      className="modal-title mb-1"
                      id={`${instanceId}-resident-profile-title`}
                    >
                      {selectedResidentName}
                    </h5>
                    <div className="small">
                      Resident profile
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close me-0"
                    aria-label="Close"
                    style={{ boxSizing: "content-box" }}
                    onClick={closeProfileModal}
                  />
                </div>
              </div>

              <div className="modal-body">
                {isProfileLoading ? (
                  <div className="d-flex align-items-center gap-2">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    Loading resident profile...
                  </div>
                ) : null}

                {!isProfileLoading && profileError ? (
                  <div className="alert alert-warning mb-0" role="alert">
                    {profileError}
                  </div>
                ) : null}

                {!isProfileLoading && !profileError ? (
                  <div className="row g-4">
                    <div className="col-12 col-lg-4">
                      <div className="card h-100">
                        <div className="card-body text-center">
                          <img
                            src={profileImageSrc}
                            alt={selectedResidentName}
                            className="img-fluid rounded-3 border"
                            style={{ maxHeight: "420px", width: "100%", objectFit: "cover" }}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = PROFILE_FALLBACK_IMAGE;
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-8">
                      <div className="card h-100">
                        <div className="card-body">
                          <dl className="row mb-0">
                            <dt className="col-sm-4">First name</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.first_name || <span>-</span>}
                            </dd>

                            <dt className="col-sm-4">Last name</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.last_name || <span>-</span>}
                            </dd>

                            <dt className="col-sm-4">Neighborhood</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.neighborhood_name || <span>-</span>}
                            </dd>

                            <dt className="col-sm-4">Email</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.user_email ? (
                                <a href={`mailto:${selectedResidentProfile.user_email}`}>
                                  {selectedResidentProfile.user_email}
                                </a>
                              ) : (
                                <span>-</span>
                              )}
                            </dd>

                            <dt className="col-sm-4">Home phone</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.phone_home || <span>-</span>}
                            </dd>

                            <dt className="col-sm-4">Mobile phone</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.mobile_phone || <span>-</span>}
                            </dd>

                            <dt className="col-sm-4">Address</dt>
                            <dd className="col-sm-8">
                              {selectedResidentProfile?.address || <span>-</span>}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={closeProfileModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 19999 }}
          onMouseDown={closeProfileModal}
        />
      </>
    ) : null;

  if (!isInEditor && !isResidentDirectoryPage) {
    return null;
  }

  if (isInEditor) {
    return (
      <section className={classes.section}>
        <div className={classes.container}>
          <h2 className={classes.title}>{title}</h2>
          <div className={classes.info} role="status">
            This section only renders on the live <code>/resident-directory</code> page. Search and live
            resident data are disabled in edit mode.
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className={classes.section}>
        <div className={classes.container}>
          <h2 className={classes.title}>{title}</h2>

        <div className={classes.searchWrap}>
          <div className={classes.searchBody}>
            <div className={classes.searchControls}>
              <div className={classes.searchTextColumn}>
                <label className={classes.searchLabel} htmlFor={`${instanceId}-search`}>
                  Search residents
                </label>
                <input
                  id={`${instanceId}-search`}
                  type="search"
                  className={classes.searchInput}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by first name, last name, or address"
                  autoComplete="off"
                />
              </div>
              <div className={classes.searchFilterColumn}>
                <label className={classes.searchLabel} htmlFor={`${instanceId}-neighborhood`}>
                  Neighborhood
                </label>
                <select
                  id={`${instanceId}-neighborhood`}
                  className={classes.searchSelect}
                  value={selectedNeighborhood}
                  onChange={(event) => setSelectedNeighborhood(event.target.value)}
                  disabled={isLoading || neighborhoodOptions.length === 0}
                >
                  <option value="">All neighborhoods</option>
                  {neighborhoodOptions.map((neighborhood) => (
                    <option key={neighborhood.value} value={neighborhood.value}>
                      {neighborhood.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={classes.helperText}>
              Enter at least {MIN_SEARCH_LENGTH} characters to search by first name, last name, or address,
              or filter by neighborhood. Multiple words match any term.
            </div>
          </div>
        </div>

        {errorText ? (
          <div className={classes.error} role="alert">
            {errorText}
          </div>
        ) : null}

        {!errorText && isLoading ? (
          <div className={classes.loading}>
            <div className={classes.loadingBody}>
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Loading resident directory...
            </div>
          </div>
        ) : null}

        {!errorText && !isLoading && items.length === 0 ? (
          <div className={classes.empty} role="status">
            No residents are available at this time.
          </div>
        ) : null}

        {!errorText && !isLoading && hasActiveFilters && totalResults === 0 ? (
          <div className={classes.empty} role="status">
            No residents matched the current filters.
          </div>
        ) : null}

        {!errorText && !isLoading && totalResults > 0 ? (
          <>
            <div className={classes.summary}>
              <span>
                Showing {rangeStart}-{rangeEnd} of {totalResults} results
              </span>
              <span>
                Page {safeCurrentPage} of {totalPages}
              </span>
            </div>

            <div className={classes.tableWrap}>
              <div className="table-scroll">
                <table className={classes.table}>
                <thead className="text-bg-white sticky-top shadow-sm">
                  <tr>
                    <th scope="col">Last Name</th>
                    <th scope="col">First Name</th>
                    <th scope="col">Neighborhood</th>
                    <th scope="col">Email</th>
                    <th scope="col">Mobile</th>
                    <th scope="col">Address</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((item, idx) => {
                    const residentId = getResidentRecordId(item);
                    const residentName = getResidentName(item);
                    const email = String(item.user_email ?? "").trim();
                    const phone = String(item.mobile_phone ?? "").trim() || String(item.phone_home ?? "").trim();
                    const neighborhoodNumber = String(item.neighborhood_number ?? "").trim();
                    const neighborhoodName = String(item.neighborhood_name ?? "").trim();

                    return (
                      <tr
                        key={item.id ?? item.item_id ?? idx}
                        role="button"
                        tabIndex={residentId ? 0 : -1}
                        aria-label={residentId ? `View profile for ${residentName}` : undefined}
                        className={residentId ? "cursor-pointer" : undefined}
                        style={residentId ? { cursor: "pointer" } : undefined}
                        onClick={() => {
                          if (residentId) {
                            openProfileModal(item);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (!residentId) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openProfileModal(item);
                          }
                        }}
                      >
                        <td>{item.last_name || <span>-</span>}</td>
                        <td>{item.first_name || <span>-</span>}</td>
                        <td className="text-nowrap">
                          {neighborhoodName || neighborhoodNumber || <span>-</span>}
                        </td>
                        <td className="text-nowrap">
                          {email ? (
                            // keep the row's open-profile handlers from firing, like the roster
                            <a
                              href={`mailto:${email}`}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}
                            >
                              {email}
                            </a>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="text-nowrap">{phone || <span>-</span>}</td>
                        <td>{item.address || <span>-</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
              <TableInteractionNote />
            </div>

            {totalPages > 1 ? (
              <nav className={classes.paginationWrap} aria-label="Resident directory pagination">
                <ul className={classes.pagination}>
                  <li className={`page-item ${safeCurrentPage <= 1 ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={safeCurrentPage <= 1}
                    >
                      Prev
                    </button>
                  </li>

                  {visiblePages.map((page, idx) => {
                    if (typeof page !== "number") {
                      return (
                        <li key={`${page}-${idx}`} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }

                    const isActive = page === safeCurrentPage;
                    return (
                      <li key={page} className={`page-item ${isActive ? "active" : ""}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                          aria-current={isActive ? "page" : undefined}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${safeCurrentPage >= totalPages ? "disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={safeCurrentPage >= totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            ) : null}
          </>
        ) : null}
        </div>
      </section>

      {profileModal && typeof document !== "undefined" ? createPortal(profileModal, document.body) : null}
    </>
  );
}

export default ResidentDirectorySection;
